import {
  // eslint-disable-next-line no-restricted-imports
  Typography as MuiTypography,
} from '@mui/material';
import { useTypographyOverflow } from '@/components/atoms/Typography/hooks';
import { TypographyProps, TypographyVariant } from '@/components/atoms/Typography/types';
import { getPaletteColor, restrictedSxPropertiesError } from '@/components/atoms/Typography/utils';
import { variants } from '@/theme/typography';
import { mergeSx } from '@/utils/cssStyles';

const variantMapping: Record<TypographyVariant, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'p',
  subtitle2: 'p',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
};

/**
 * Typography is a design system component atom for rendering text.
 *
 * **Important note:** If you need to give strong importance to the text, use the \<strong> HTML5 tag to wrap the
 * text content of this component.
 *
 * @param {Object} props - TypographyProps.
 * @returns {React.Element} The configured Typography component wrapped in Material-UI's MuiTypography.
 */
export function PTypography({
  children,
  align,
  title,
  variant,
  sx,
  color,
  noOverflow,
  ref,
  'aria-current': ariaCurrent,
}: TypographyProps) {
  restrictedSxPropertiesError(sx);

  const { overflowSx } = useTypographyOverflow({
    noOverflow,
  });

  return (
    <MuiTypography
      ref={ref}
      align={align}
      aria-current={ariaCurrent}
      title={noOverflow?.title ?? title}
      variant={variant}
      sx={mergeSx(variants[variant], sx, overflowSx)}
      color={getPaletteColor(color)}
      variantMapping={variantMapping}
    >
      {children}
    </MuiTypography>
  );
}
